import { InputCheckbox } from '@creditornot/cb-checkbox';
import { cssThemedValue, Typography } from '@creditornot/cb-ingredients';
import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  BasketDiscountType,
  CampaignConfiguration,
  DiscountConfig,
} from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import validateYupSchema from 'common/utils/validateYupSchema';
import { TERMS_AND_CONDITIONS_LINK } from 'modules/campaigns/constants';
import { useCampaignModuleContext } from 'modules/campaigns/context';

import {
  BasketDiscountFormValues,
  CampaignAction,
  CampaignRulesFormValues,
  CampaignStatus,
  VenuesValidationError,
} from '../../../types';
import { getBasketDiscountValue } from '../../../utils/getBasketDiscountValue';
import { shakingAnimationMixin } from '../../../utils/shakingCssAnimation';
import { CampaignsTargetContent } from '../../../utils/telemetry/constants';
import { telemetryNewCampaignInteractionClickCampaignRules } from '../../../utils/telemetry/telemetry';
import { TestIds } from '../../../utils/testUtils';
import { basketDiscountFormValidationSchema } from '../../../utils/validationSchemas/basketDiscountFormValidationSchema';
import { CampaignActionsFooter } from '../../CampaignActionsFooter/CampaignActionsFooter';
import CampaignFormLayout from '../../CampaignFormLayout';
import CopyCampaignIdButton from '../../CopyCampaignIdButton';
import { BasketDiscountTypeFormItem } from '../../basket-discount-type-section/BasketDiscountTypeFormItem/BasketDiscountTypeFormItem';
import { BudgetFormItem } from '../../budget-section/BudgetFormItem/BudgetFormItem';
import { DiscountAmountFormItem } from '../../discount-amount-section/DiscountAmountFormItem/DiscountAmountFormItem';
import BasketDiscountPreview from '../../preview/BasketDiscount/BasketDiscountPreview';
import BasketDiscountVariantText from '../../preview/BasketDiscount/BasketDiscountVariantText';
import { YuhoCampaignBanner } from '../../preview/PromotionBanner/YuhoCampaignBanner';
import RulesFormItem from '../../rules-section/RulesFormItem';
import { CampaignSchedulingFormItem } from '../../scheduling-section/CampaignSchedulingFormItem/CampaignSchedulingFormItem';
import { VenuesFormItem } from '../../venues-section/VenuesFormItem';

export type BasketDiscountFormProps = {
  venueDetails?: Pick<Venue, 'name' | 'short_description' | 'listimage'>;
  campaignId?: string;
  campaignStatus?: CampaignStatus;
  totalSpentBudget?: number;
  defaultValues: BasketDiscountFormValues;
  configuration: CampaignConfiguration;
  timezone: string;
  country: Country;
  isLoading: boolean;
  venuesValidationErrors?: VenuesValidationError;
  availableActions?: CampaignAction[];
  onSubmit: (values: BasketDiscountFormValues) => void;
  onClone?: (campaignId: string) => void;
  onDelete?: (campaignId: string) => void;
  onTerminate?: (campaignId: string) => void;
  onCancelEdit?: () => void;
};

const StyledCheckboxLabel = styled.label<{ isShaking?: boolean }>`
  display: flex;
  gap: 8px;
  color: ${cssThemedValue('textPrimary')};
  ${({ isShaking }) => (isShaking ? shakingAnimationMixin : 'animation: none')}
`;

const StyledWrapper = styled.div``;

const StyledAgreementsWrapper = styled.div`
  display: flex;
  border-radius: 8px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 16px 0;
`;

const BasketDiscountForm = ({
  venueDetails,
  campaignId,
  campaignStatus,
  defaultValues,
  isLoading,
  onSubmit,
  configuration,
  timezone,
  country,
  venuesValidationErrors,
  availableActions,
  onClone,
  onDelete,
  onTerminate,
  onCancelEdit,
}: BasketDiscountFormProps) => {
  const [shouldShake, setShouldShake] = useState<boolean>(false);
  const { currency } = useCampaignModuleContext();
  const minBasketSizeOptions = useMemo(
    () => Object.keys(configuration.basket_discounts).map(size => Number(size)),
    [configuration],
  );
  const isExistingRunningCampaign = campaignStatus === CampaignStatus.RUNNING && !!campaignId;
  const { entityType } = useCampaignModuleContext();
  const isMerchantLevel = entityType === 'merchant';

  const handleSubmitCampaign = (values: BasketDiscountFormValues) => {
    if (values.agreementTermsAndConditions) {
      return onSubmit({ ...values, campaignId });
    }

    setShouldShake(false);
    // Timeout to prevent react from batching state setters
    setTimeout(() => setShouldShake(true), 0);
  };

  return (
    <StyledWrapper data-testid={TestIds.FORM.WRAPPER}>
      <Form<BasketDiscountFormValues>
        onSubmit={handleSubmitCampaign}
        initialValues={defaultValues}
        validate={validateYupSchema(basketDiscountFormValidationSchema)}
      >
        {({ handleSubmit, form, values }) => {
          const discountConfig: DiscountConfig = configuration.basket_discounts?.[values.minBasketSize || 0];

          const campaignBanner = (
            <YuhoCampaignBanner>
              <BasketDiscountVariantText
                discountType={values.discountType}
                discountValue={values.discountValue}
                minBasketSize={values.minBasketSize}
              />
            </YuhoCampaignBanner>
          );

          return (
            <CampaignFormLayout
              isFloatingBannerEnabled
              preview={
                <BasketDiscountPreview
                  venueDetails={venueDetails}
                  banner={campaignBanner}
                  startDate={values.startDate}
                  endDate={values.endDate}
                  timezone={timezone}
                  totalBudgetSum={values.totalBudgetSum}
                  firstPurchaseOnly={values.firstPurchaseOnly}
                />
              }
              onFormSubmit={handleSubmit}
              banner={campaignBanner}
              tooltip="campaigns.sidebar-preview.product.tooltip"
            >
              {isMerchantLevel && (
                <VenuesFormItem
                  initialValues={{
                    venueIds: values.venueIds,
                  }}
                  startDate={values.startDate}
                  endDate={values.endDate}
                  campaignType={values.campaignType}
                  isDisabled={isExistingRunningCampaign}
                  venuesValidationErrors={venuesValidationErrors}
                  onUpdate={(venueIds: string[]) => form.change('venueIds', venueIds)}
                />
              )}
              <BasketDiscountTypeFormItem
                data-testid={TestIds.FORM.DISCOUNT_TYPE.WRAPPER}
                campaignType={defaultValues.campaignType}
                onChange={(discountType: BasketDiscountType) => {
                  form.change(
                    'discountValue',
                    getBasketDiscountValue(discountType, {
                      amount: discountConfig.recommended_amount,
                      percentage: discountConfig.recommended_percentage,
                      upperLimit: discountConfig.upper_limits[0],
                    }),
                  );
                }}
                isNewCampaignRoute={!campaignId}
                isDisabled={isExistingRunningCampaign}
              />

              <RulesFormItem
                data-testid={TestIds.FORM.RULES.WRAPPER}
                minBasketSizeOptions={minBasketSizeOptions}
                initialValues={{
                  minBasketSize: values.minBasketSize,
                  firstPurchaseOnly: values.firstPurchaseOnly,
                  usageType: values.usageType,
                  perUserUsageLimit: values.perUserUsageLimit,
                  isMinBasketIncreased: values.minBasketSize !== minBasketSizeOptions[0],
                }}
                onUpdate={(rulesValues: CampaignRulesFormValues) => {
                  form.batch(() => {
                    if (values.minBasketSize !== rulesValues.minBasketSize) {
                      const newDiscountConfig = configuration.basket_discounts[rulesValues.minBasketSize || 0];

                      form.change(
                        'discountValue',
                        getBasketDiscountValue(values.discountType, {
                          amount: newDiscountConfig.recommended_amount,
                          percentage: newDiscountConfig.recommended_percentage,
                          upperLimit: newDiscountConfig.upper_limits[0],
                        }),
                      );
                    }

                    form.change('minBasketSize', rulesValues.minBasketSize);
                    form.change('firstPurchaseOnly', rulesValues.firstPurchaseOnly);
                    form.change('usageType', rulesValues.usageType);
                    form.change('perUserUsageLimit', rulesValues.perUserUsageLimit);
                  });
                }}
                campaignType={defaultValues.campaignType}
                isExistingRunningCampaign={isExistingRunningCampaign}
                isNewCampaignRoute={!campaignId}
              />

              {values.minBasketSize !== undefined && discountConfig && (
                <DiscountAmountFormItem
                  data-testid={TestIds.FORM.DISCOUNT_AMOUNT.WRAPPER}
                  onUpdate={discountAmountValues => {
                    form.change(
                      'discountValue',
                      getBasketDiscountValue(values.discountType, {
                        amount: discountAmountValues.discountAmount,
                        percentage: discountAmountValues.discountAmount,
                        upperLimit: discountAmountValues.upperLimit,
                      }),
                    );
                  }}
                  discountType={values.discountType}
                  minBasketSize={convertMonetaryIntToDecimal(values.minBasketSize, currency)}
                  discountValue={values.discountValue}
                  discountValueOptions={{
                    [BasketDiscountType.AMOUNT]: discountConfig.amounts,
                    [BasketDiscountType.PERCENTAGE]: discountConfig.percentages,
                  }}
                  recommendedDiscountValue={{
                    [BasketDiscountType.AMOUNT]: discountConfig.recommended_amount,
                    [BasketDiscountType.PERCENTAGE]: discountConfig.recommended_percentage,
                  }}
                  upperLimitOptions={discountConfig.upper_limits}
                  recommendedUpperLimit={discountConfig.upper_limits[0]}
                  campaignType={defaultValues.campaignType}
                  isNewCampaignRoute={!campaignId}
                  isDisabled={isExistingRunningCampaign}
                />
              )}

              <BudgetFormItem
                data-testid={TestIds.FORM.BUDGET.WRAPPER}
                isDisabled={isExistingRunningCampaign}
                recommendedBudget={convertMonetaryIntToDecimal(configuration.default_total_budget_sum, currency)}
              />

              <CampaignSchedulingFormItem
                data-testid={TestIds.FORM.SCHEDULING.WRAPPER}
                timezone={timezone}
                country={country}
                startDate={values.startDate}
                endDate={values.endDate}
                weeklySchedule={values.weeklySchedule}
                campaignType={defaultValues.campaignType}
                isExistingRunningCampaign={isExistingRunningCampaign}
                isNewCampaignRoute={!campaignId}
                onUpdate={formData => {
                  form.batch(() => {
                    form.change('startDate', formData.startDate);
                    form.change('endDate', formData.endDate);
                    form.change('weeklySchedule', formData.weeklySchedule);
                  });
                }}
              />
              <StyledAgreementsWrapper data-testid={TestIds.FORM.AGREEMENTS.WRAPPER}>
                <Field<BasketDiscountFormValues['agreementTermsAndConditions']>
                  name="agreementTermsAndConditions"
                  type="checkbox"
                >
                  {({ input }) => (
                    <StyledCheckboxLabel isShaking={shouldShake && !input.checked}>
                      <InputCheckbox
                        name={input.name}
                        checked={input.checked}
                        onChange={input.onChange}
                        disabled={!!campaignId}
                        data-testid={TestIds.FORM.AGREEMENTS.AGRREMENT_TERMS_AND_CONDITIONS}
                      />
                      <Typography.Body3>
                        <FormattedMessage
                          id="campaigns.agreements.terms-and-conditions-statement"
                          values={{
                            link: (
                              <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                                <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                              </a>
                            ),
                          }}
                        />
                      </Typography.Body3>
                    </StyledCheckboxLabel>
                  )}
                </Field>
              </StyledAgreementsWrapper>

              <CampaignActionsFooter
                campaignId={campaignId}
                isLoading={isLoading}
                availableActions={availableActions}
                onDelete={onDelete}
                onTerminate={onTerminate}
                onClone={onClone}
                onUpdate={() => handleSubmit()}
                onCreate={async () => {
                  if (!campaignId) {
                    telemetryNewCampaignInteractionClickCampaignRules(
                      CampaignsTargetContent.NEW.CREATE_CAMPAIGN,
                      defaultValues.campaignType,
                    );
                  }
                  await handleSubmit();
                }}
                onCancel={onCancelEdit}
                isEditMode
              />

              {campaignId && <CopyCampaignIdButton dataTestId={TestIds.FORM.ID.WRAPPER} campaignId={campaignId} />}
            </CampaignFormLayout>
          );
        }}
      </Form>
    </StyledWrapper>
  );
};

export default BasketDiscountForm;
